// useAutoRefresh
// --------------
// Keeps a list screen "live" without a manual reload. Polls `refresh` on a timer
// while the screen is mounted, and ALSO fires it immediately when the tab regains
// visibility (so returning to a backgrounded tab catches up instantly).
//
// The poll runs regardless of tab visibility (John, 31 Jul 2026): the old
// "skip the tick while hidden" guard silently stopped ALL refreshes whenever the
// environment reported the page as hidden — some webviews / always-backgrounded
// windows do this even while the user is looking at it — so changes never appeared
// until a manual reload. A poll on a genuinely backgrounded tab is a cheap, harmless
// fetch; keeping the list actually live is worth it.
//
// The latest `refresh` is held in a ref so a changing callback identity (a new
// closure every render) doesn't tear down and rebuild the interval.
//
// Note: this does NOT fire an initial refresh on mount — each screen already
// does its own initial load. This hook only handles the ongoing refresh.
//
// TypeScript source — useAutoRefresh.js is a thin re-export stub.

import { useEffect, useRef } from "react";

export interface AutoRefreshOptions {
  // How often to poll while the tab is visible. Default 30s.
  intervalMs?: number;
  // Set false to suspend auto-refresh (e.g. a modal is open). Default true.
  enabled?: boolean;
}

export function useAutoRefresh(refresh: () => void, options: AutoRefreshOptions = {}) {
  const { intervalMs = 30000, enabled = true } = options;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(
    function () {
      if (!enabled) return;

      function tick() {
        // Always poll while mounted — do NOT gate on visibilityState (a misreported
        // "hidden" would silently stop every refresh; see the header note).
        refreshRef.current();
      }
      function onVisibilityChange() {
        if (document.visibilityState === "visible") refreshRef.current();
      }

      const id = setInterval(tick, intervalMs);
      document.addEventListener("visibilitychange", onVisibilityChange);
      return function () {
        clearInterval(id);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    },
    [intervalMs, enabled]
  );
}
